import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/shared/ui/components/button';
import { DialogFooter } from '@/shared/ui/components/dialog';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import { Forbidden } from '@/modules/auth/Forbidden';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { useVenueContext } from '@/modules/venue/VenueContext';
import { apiErrorMessage } from '@/shared/api-client/workspace';
import {
  useCreatePosStation,
  usePosStations,
  useRenamePosStation,
  useSetPosStationArchived,
  type PosDevice,
} from './posStations';

/** Maps a backend error code to a translated, user-facing message. */
export function PosStationsPage() {
  const auth = useAuthorization();
  const { venueId } = useVenueContext();
  const canManage = auth.can('pos.device.manage', { kind: 'venue', venueId });

  if (!canManage) return <Forbidden />;

  return <PosStationsManager venueId={venueId} />;
}

function PosStationsManager({ venueId }: { venueId: string }) {
  const { t } = useTranslation('common');
  const [editing, setEditing] = useState<PosDevice | 'new' | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);

  const { data, isLoading, isError } = usePosStations(venueId, includeArchived);
  const setArchived = useSetPosStationArchived(venueId);

  async function toggleArchived(device: PosDevice) {
    try {
      await setArchived.mutateAsync({ deviceId: device.id, archived: !device.archived });
      toast.success(
        t(device.archived ? 'posStations.toast.restored' : 'posStations.toast.archived'),
      );
    } catch (caught) {
      toast.error(apiErrorMessage(t, 'posStations', caught));
    }
  }

  if (editing !== null) {
    return (
      <PosStationForm
        venueId={venueId}
        device={editing === 'new' ? null : editing}
        onDone={() => setEditing(null)}
      />
    );
  }

  return (
    <section className="grid gap-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">{t('posStations.pageTitle')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('posStations.description')}</p>
      </div>

      <div className="flex items-center justify-between gap-3">
        <Button size="sm" onClick={() => setEditing('new')}>
          {t('posStations.actions.create')}
        </Button>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(event) => setIncludeArchived(event.target.checked)}
          />
          {t('posStations.showArchived')}
        </label>
      </div>

      {isLoading && <p className="text-muted-foreground">{t('posStations.loading')}</p>}
      {isError && <p className="text-destructive">{t('posStations.errors.loadFailed')}</p>}
      {data && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">{t('posStations.columns.name')}</th>
                <th className="px-4 py-2 font-medium">{t('posStations.columns.status')}</th>
                <th className="px-4 py-2 font-medium">{t('posStations.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-muted-foreground">
                    {t('posStations.empty')}
                  </td>
                </tr>
              )}
              {data.items.map((device) => (
                <tr key={device.id} className="border-b last:border-0">
                  <td className="px-4 py-2 font-medium">{device.name}</td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {t(
                      device.archived ? 'posStations.status.archived' : 'posStations.status.active',
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setEditing(device)}>
                        {t('posStations.actions.edit')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={setArchived.isPending}
                        onClick={() => void toggleArchived(device)}
                      >
                        {t(
                          device.archived
                            ? 'posStations.actions.restore'
                            : 'posStations.actions.archive',
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function PosStationForm({
  venueId,
  device,
  onDone,
}: {
  venueId: string;
  device: PosDevice | null;
  onDone: () => void;
}) {
  const { t } = useTranslation('common');
  const create = useCreatePosStation(venueId);
  const rename = useRenamePosStation(venueId);
  const [name, setName] = useState(device?.name ?? '');
  const [error, setError] = useState<string | null>(null);

  const pending = create.isPending || rename.isPending;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      if (device) {
        await rename.mutateAsync({ deviceId: device.id, name });
        toast.success(t('posStations.toast.renamed'));
      } else {
        await create.mutateAsync(name);
        toast.success(t('posStations.toast.created'));
      }
      onDone();
    } catch (caught) {
      setError(apiErrorMessage(t, 'posStations', caught));
    }
  }

  return (
    <form onSubmit={submit} className="grid gap-4 p-6">
      <div className="grid gap-2">
        <Label htmlFor="pos-station-name">{t('posStations.fields.name')}</Label>
        <Input
          id="pos-station-name"
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onDone}>
          {t('actions.cancel')}
        </Button>
        <Button type="submit" disabled={pending}>
          {pending
            ? t('posStations.form.submitting')
            : device
              ? t('posStations.form.save')
              : t('posStations.form.create')}
        </Button>
      </DialogFooter>
    </form>
  );
}
